import { ROUTE_MANIFEST } from '../data/routeManifest';

/** 사전 매핑 — 자주 발생하는 오입력 → 정답 경로 (queue #680 §R680-4, 정확 별칭 매치만) */
const ALIAS_MAP: Record<string, string> = {
  '/project-scaffold': '/scaffold',
  '/terminology': '/admin/terminology',
  '/user': '/users',
  '/role': '/roles',
  '/menu': '/menus',
  '/dashboard': '/_index',
};

export interface RouteSuggestion {
  path: string;
  /** 정규화 유사도 0~1. 사전 매핑 매치는 1. */
  score: number;
}

const SIMILARITY_THRESHOLD = 0.6;
const MAX_EDIT_DISTANCE = 2;
const MAX_SUGGESTIONS = 3;

function levenshteinDistance(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] =
        a[i - 1] === b[j - 1]
          ? dp[i - 1][j - 1]
          : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[m][n];
}

/**
 * 404 유사 라우트 제안 — queue #680 §R680-4.
 *
 * 1) 사전 매핑 우선 — `ALIAS_MAP` 에 정확 일치하는 별칭이 있으면 그 1건만 반환하고
 *    레벤슈타인 fallback 은 실행하지 않는다.
 * 2) 레벤슈타인 fallback — 매니페스트 각 경로와의 편집 거리로 정규화 유사도를 계산해
 *    유사도 ≥ 0.6 또는 편집 거리 ≤ 2 인 상위 최대 3건을 유사도 내림차순으로 반환.
 *    임계 미달이면 빈 배열(제안 없음).
 */
export function suggestRoute(
  pathname: string,
  manifest: readonly string[] = ROUTE_MANIFEST
): RouteSuggestion[] {
  const alias = ALIAS_MAP[pathname];
  if (alias) {
    return [{ path: alias, score: 1 }];
  }

  return manifest
    .map((path) => {
      const distance = levenshteinDistance(pathname, path);
      const maxLen = Math.max(pathname.length, path.length, 1);
      const score = 1 - distance / maxLen;
      return { path, score, distance };
    })
    .filter((c) => c.score >= SIMILARITY_THRESHOLD || c.distance <= MAX_EDIT_DISTANCE)
    .sort((a, b) => b.score - a.score)
    .slice(0, MAX_SUGGESTIONS)
    .map(({ path, score }) => ({ path, score }));
}
